import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import Cookies from "js-cookie"

import getApiErrorMessages from "@utils/getApiErrorMessages"

import toast from "@molecules/Toast/Toast"

import { CRM_ENDPOINTS } from "@endpoints/crm"

import { CRMApiErrorResponse, CRMConfigPayload } from "./SettingsCRM.types"

export const crmConnectionQueryKey = ["crm-connection", CRM_ENDPOINTS.CONNECTION]
export const crmConfigQueryKey = ["crm-config", CRM_ENDPOINTS.CONFIG]

async function fetchCRMJSON<ResponseData>(endpoint: string): Promise<ResponseData> {
    const response = await fetch(endpoint)

    if (!response.ok) {
        throw new Error(`Request to ${endpoint} failed with status ${response.status}.`)
    }

    return (await response.json()) as ResponseData
}

async function writeCRMJSON<ResponseData>(
    endpoint: string,
    method: "POST" | "PUT",
    payload?: unknown,
): Promise<ResponseData> {
    const response = await fetch(endpoint, {
        method,
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRFToken": Cookies.get("csrftoken") ?? "",
        },
        body: payload === undefined ? undefined : JSON.stringify(payload),
    })

    if (!response.ok) {
        throw (await response.json()) as CRMApiErrorResponse
    }

    return (await response.json()) as ResponseData
}

export function useCRMConnectionQuery() {
    return useQuery({
        queryKey: crmConnectionQueryKey,
        queryFn: () => fetchCRMJSON<CRMConnectionState>(CRM_ENDPOINTS.CONNECTION),
    })
}

export function useCRMConfigQuery() {
    return useQuery({
        queryKey: crmConfigQueryKey,
        queryFn: () => fetchCRMJSON<CRMConfig>(CRM_ENDPOINTS.CONFIG),
    })
}

export function useCRMConfigMutation() {
    const client = useQueryClient()

    return useMutation<CRMConfig, CRMApiErrorResponse, CRMConfigPayload>({
        mutationFn: (crmConfig) => writeCRMJSON<CRMConfig>(CRM_ENDPOINTS.CONFIG, "PUT", crmConfig),

        // Cancel an in-flight read so it cannot land after the write and restore the old values.
        onMutate: async () => {
            await client.cancelQueries({ queryKey: crmConfigQueryKey })
        },

        // The write answers with the saved configuration, so the cache takes it without a re-read.
        onSuccess: (updatedConfig) => {
            client.setQueryData(crmConfigQueryKey, updatedConfig)

            toast({ type: "success", title: "CRM sync settings updated" })
        },
        onError: (error) => {
            const messages = getApiErrorMessages(error.non_field_errors)
            toast({ type: "error", title: messages[0] ?? "Could not update the CRM settings" })
        },
    })
}

export function useCRMRecordConnectionMutation() {
    const client = useQueryClient()

    return useMutation<CRMConnectionState, CRMApiErrorResponse, void>({
        mutationFn: () => writeCRMJSON<CRMConnectionState>(CRM_ENDPOINTS.CONNECTION, "POST"),
        onSuccess: (connection) => {
            // The answer is the connection just recorded, so it replaces the cached one without another read.
            client.setQueryData(crmConnectionQueryKey, connection)

            // The configuration reports the recorded connector too, so it is read again.
            void client.invalidateQueries({ queryKey: crmConfigQueryKey })
        },
        onError: (error) => {
            const messages = getApiErrorMessages(error.non_field_errors)
            toast({ type: "error", title: messages[0] ?? "Could not record the CRM connection" })
        },
    })
}
